export type GridRgb = readonly [number, number, number]
export type GridCursorStyle = 'block' | 'bar' | 'underline'
export type GridFontVariant = 'regular' | 'bold' | 'italic' | 'bold-italic'

export interface GridFontConfig {
  readonly variant: GridFontVariant
  readonly hash: string
  readonly byteLength: number
  readonly embedded: boolean
}

export interface GridRendererConfig {
  readonly configGeneration: number
  readonly profileFontSize: number
  readonly fontSize: number
  /**
   * The px size to hand a CSS/em-based font engine. NOT interchangeable with
   * fontSize: the server sizes by (ascent - descent), a font-specific multiple
   * of the em (1.32x for JetBrains Mono). Rasterizing at fontSize draws glyphs
   * that overflow and clip against the cellWidth/cellHeight declared here.
   */
  readonly cssPxSize: number
  readonly lineHeight: number
  readonly cellWidth: number
  readonly cellHeight: number
  readonly baseline: number
  readonly palette: readonly GridRgb[]
  readonly foreground: GridRgb
  readonly background: GridRgb
  readonly cursor: GridRgb
  readonly cursorStyle: GridCursorStyle
  readonly cursorBlink: boolean
  readonly fonts: readonly GridFontConfig[]
}

const CONFIG_KEYS = [
  'type', 'config_generation', 'profile_font_size', 'font_size', 'css_px_size',
  'line_height', 'cell_width', 'cell_height', 'baseline', 'palette', 'foreground',
  'background', 'cursor', 'cursor_style', 'cursor_blink', 'fonts',
] as const
const FONT_KEYS = ['variant', 'hash', 'byte_length', 'embedded'] as const
const FONT_VARIANTS = new Set<GridFontVariant>(['regular', 'bold', 'italic', 'bold-italic'])
const CURSOR_STYLES = new Set<GridCursorStyle>(['block', 'bar', 'underline'])
const MAX_FONT_SOURCE_BYTES = 32 * 1024 * 1024 - 44

export class GridConfigError extends Error {
  constructor(message: string) {
    super(`invalid grid configuration: ${message}`)
    this.name = 'GridConfigError'
  }
}

function fail(message: string): never {
  throw new GridConfigError(message)
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) fail(`${label} is not an object`)
  return value as Record<string, unknown>
}

function exactKeys(value: Record<string, unknown>, expected: readonly string[], label: string): void {
  const keys = Object.keys(value).sort()
  const wanted = [...expected].sort()
  if (keys.length !== wanted.length || keys.some((key, index) => key !== wanted[index])) {
    fail(`${label} fields do not match the protocol`)
  }
}

function integer(value: unknown, minimum: number, maximum: number, label: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < minimum || value > maximum) {
    fail(`${label} is out of bounds`)
  }
  return value
}

function rgb(value: unknown, label: string): GridRgb {
  if (!Array.isArray(value) || value.length !== 3) fail(`${label} is not RGB`)
  return Object.freeze([
    integer(value[0], 0, 255, `${label} red`),
    integer(value[1], 0, 255, `${label} green`),
    integer(value[2], 0, 255, `${label} blue`),
  ]) as GridRgb
}

function font(value: unknown): GridFontConfig {
  const source = record(value, 'font')
  exactKeys(source, FONT_KEYS, 'font')
  const variant = source.variant
  if (typeof variant !== 'string' || !FONT_VARIANTS.has(variant as GridFontVariant)) fail('font variant is invalid')
  const hash = source.hash
  if (typeof hash !== 'string' || !/^[0-9a-f]{64}$/u.test(hash)) fail('font hash is invalid')
  const embedded = source.embedded
  if (typeof embedded !== 'boolean' || (embedded && variant !== 'regular')) fail('font embedded flag is invalid')
  return Object.freeze({
    variant: variant as GridFontVariant,
    hash,
    byteLength: integer(source.byte_length, 1, MAX_FONT_SOURCE_BYTES, 'font byte length'),
    embedded,
  })
}

export function parseGridConfig(value: unknown): GridRendererConfig {
  const source = record(value, 'message')
  exactKeys(source, CONFIG_KEYS, 'message')
  if (source.type !== 'grid_config') fail('message type is invalid')
  if (typeof source.line_height !== 'number' || !Number.isFinite(source.line_height) || source.line_height <= 0) {
    fail('line height is invalid')
  }
  if (typeof source.css_px_size !== 'number' || !Number.isFinite(source.css_px_size) || source.css_px_size <= 0) {
    fail('css px size is invalid')
  }
  const cursorStyle = source.cursor_style
  if (typeof cursorStyle !== 'string' || !CURSOR_STYLES.has(cursorStyle as GridCursorStyle)) fail('cursor style is invalid')
  if (typeof source.cursor_blink !== 'boolean') fail('cursor blink is invalid')
  if (!Array.isArray(source.palette) || source.palette.length !== 256) fail('palette must contain 256 colors')
  if (!Array.isArray(source.fonts) || source.fonts.length < 1 || source.fonts.length > 4) fail('font list is invalid')
  const fonts = source.fonts.map(font)
  const variants = new Set(fonts.map((entry) => entry.variant))
  if (variants.size !== fonts.length || !variants.has('regular')) fail('font variants are missing or duplicated')

  return Object.freeze({
    configGeneration: integer(source.config_generation, 1, 0xffffffff, 'configuration generation'),
    // This is the user's raw uint16 preference. Only the active font size is
    // clamped; reset requests need the original value to remain intact.
    profileFontSize: integer(source.profile_font_size, 0, 0xffff, 'profile font size'),
    fontSize: integer(source.font_size, 6, 96, 'font size'),
    cssPxSize: source.css_px_size,
    lineHeight: source.line_height,
    cellWidth: integer(source.cell_width, 1, 0xffff, 'cell width'),
    cellHeight: integer(source.cell_height, 1, 0xffff, 'cell height'),
    baseline: integer(source.baseline, -0x8000, 0x7fff, 'baseline'),
    palette: Object.freeze(source.palette.map((entry, index) => rgb(entry, `palette ${index}`))),
    foreground: rgb(source.foreground, 'foreground'),
    background: rgb(source.background, 'background'),
    cursor: rgb(source.cursor, 'cursor'),
    cursorStyle: cursorStyle as GridCursorStyle,
    cursorBlink: source.cursor_blink,
    fonts: Object.freeze(fonts),
  })
}
